import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IImageRendererProps } from './models';
import { classnames } from '@src/utils/classnames';
import { getClass } from '@src/utils/getClass';
import { AppContext } from '@src/providers/App';
import { getDefaultProps, getIsExternalSrc } from './utils';

import styled from './ImageRenderer.scss';

/**
 * Загружает изображение только когда оно попадает в видимую область просмотра.
 * При этом сперва загружается уменьшенное изображение `thumb` и только потом основное.
 *
 * Если возникает ошибка при загрузке основного изображения будет показан thumb
 */
export const ImageRenderer: FC<IImageRendererProps> = ({
  url,
  thumb,
  fit = 'cover',
  className,
  containerClass,
  fullFillWidth,
  fullFillHeight,
  uploadImmediately,
  noBlur,
  onLoaded,
  onLoad,
  greyscale = false,
  alt = 'default',
  defaultTag,
  width,
  height,
  ...rest
}) => {
  const { nextComponent } = useContext(AppContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoadedThumb, setIsLoadedThumb] = useState(false);
  const [isMainVisible, setIsMainVisible] = useState(!thumb);
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [thumbRef, setThumbRef] = useState<HTMLImageElement | null>(null);

  const loadingFlag: HTMLImageElement['loading'] = uploadImmediately ? 'eager' : 'lazy';

  const containerRef = useRef<HTMLDivElement>(null);
  const isInitLoad = useRef(false);

  const handleIsLoadedThumb = useCallback(() => {
    setIsLoadedThumb(true);
    setIsMainVisible(true);
  }, []);

  const handleIsLoaded = useCallback(() => {
    if (!isInitLoad.current) {
      isInitLoad.current = true;

      onLoad?.();
    }

    setIsLoaded(true);
  }, [onLoad]);

  const handleThumbError = useCallback(() => {
    setIsMainVisible(true);
  }, []);

  const handleError = useCallback(
    (error) => {
      if (isLoadedThumb) {
        setIsMainVisible(false);
        return;
      }

      console.warn('Ошибка загрузки изображения', error);
    },
    [isLoadedThumb],
  );

  const handleOnLoaded = useCallback(() => {
    onLoaded?.();
  }, [onLoaded]);

  useEffect(() => {
    if (isLoaded) {
      handleOnLoaded();
    }
  }, [isLoaded, handleOnLoaded]);

  const classes = classnames(styled.root, greyscale && styled.greyscale, containerClass);
  const imgClasses = classnames(className, styled.image, getClass(styled, 'fit', fit), {
    [styled.isLoaded]: isLoaded,
    [styled.fullFillWidth]: fullFillWidth,
    [styled.fullFillHeight]: fullFillHeight,
  });

  const thumbClasses = classnames(styled.thumb, imgClasses, {
    [styled.withBlur]: !noBlur,
  });

  const isNext = !defaultTag && nextComponent?.Image && !getIsExternalSrc(url);
  /**
   * По умолчанию в nextjs для отображения изображений будем использовать дефолтный Image from 'next/image'
   * Актуально только для nextjs проектов
   * Здесь есть несколько замечаний. @link https://nextjs.org/docs/api-reference/next/image#width
   * - Если мы загружаем локальный рисунок, как правило тип такого рисунка будет `StaticImageData`, в таком случае передаем в url именно полностью объект с определенной шириной и высотой.
   * - Если рисунок подгружается динамически, в таком случае необходимо доработать next.config.js @link https://nextjs.org/docs/api-reference/next/image#src.
   *   В таком случае компонент ImageRenderer добавит флаг `fill` для того чтобы размеры изображения соответствовали размеру родителя
   */
  const Image = useMemo(() => (isNext ? nextComponent.Image : 'img'), [isNext, nextComponent?.Image]);

  useEffect(() => {
    if (isInitLoad.current) return;

    /** Когда изображение грузится из кеша браузера, то не вызывается событие onLoad, оно вызывается только если будет меняться
     * аттрибут src или при первоначально загрузке. Поэтому нужно проверять явно наличие флага completed и вызывать самомстоятельно колбек загрузки.
     * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/complete
     * @link https://stackoverflow.com/questions/39777833/image-onload-event-in-isomorphic-universal-react-register-event-after-image-is */
    if (imgRef?.complete) {
      isInitLoad.current = true;

      handleIsLoaded();
    }
  }, [handleIsLoaded, imgRef]);

  /** Также проверяем у thumb картинки состояние completed. Иначе основная картинка не появится. */
  useEffect(() => {
    if (thumbRef?.complete) {
      handleIsLoadedThumb();
    }
  }, [thumbRef, handleIsLoadedThumb]);

  return (
    <div className={classes} ref={containerRef}>
      {thumb && (
        <Image
          {...getDefaultProps({
            props: rest,
            isNext: !!isNext,
            src: thumb,
            alt,
            width,
            height,
          })}
          className={thumbClasses}
          onLoad={handleIsLoadedThumb}
          onError={handleThumbError}
          loading={loadingFlag}
          ref={setThumbRef}
        />
      )}
      {(isMainVisible || !thumb) && (
        <Image
          {...getDefaultProps({
            props: rest,
            isNext: !!isNext,
            src: url,
            alt,
            width,
            height,
          })}
          className={imgClasses}
          onLoad={handleIsLoaded}
          onError={handleError}
          loading={loadingFlag}
          ref={setImgRef}
        />
      )}
    </div>
  );
};
